import {
  Axis,
  AxisSortOrder,
  AxisSourceGroupKind,
  AxisSourceOption,
  AxisSourceType,
  AxisSourceValue,
  ComponentMatrix2SettingsControlsDependencies,
  ComponentMatrix2ViewSettings,
  ComponentMatrix2ViewSettingsState,
  ComponentTypeStyle,
} from './types';
import { ViewIds } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { Tag } from '@ardoq/status-ui';
import { FlexBox, Stack } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { WithPopoverPlacement } from '@ardoq/popovers';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { ensureContrast, getLightenedColor } from '@ardoq/color-helpers';
import {
  SelectedReferenceTypeInfo,
  type TraversedReferenceTypeInfo,
} from '@ardoq/settings-bar';
import {
  type SettingsConfig,
  SettingsType,
  updateViewSettings,
} from '@ardoq/view-settings';
import { dispatchAction } from '@ardoq/rxbeach';
import { localeCompare, type Locale } from '@ardoq/locale';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { DropdownItem, DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { t, type I18nLocale } from '@ardoq/i18n';
import { buildAxisDropdownOption } from './ComponentMatrix2AxisDropdownContent';
import { SelectOption } from '@ardoq/select';
import { getRedactedFieldDisplay } from '@ardoq/renderers';
import { AxisSourceGroup } from './ComponentMatrix2AxisDropdownSelectComponents';
import {
  isFieldSourceValue,
  isParentSourceValue,
  isRefSourceValue,
} from './utils';
import { isNull } from 'lodash';

const VIEW_ID = ViewIds.COMPONENT_MATRIX_2;

const buildFieldSourceValue = (fieldName: string): AxisSourceValue => ({
  type: AxisSourceType.FIELD,
  fieldName,
});

const buildReferenceSourceValue = (
  ref: SelectedReferenceTypeInfo
): AxisSourceValue => ({
  type: AxisSourceType.REF,
  ref,
});

const PARENT_SOURCE_VALUE: AxisSourceValue = { type: AxisSourceType.PARENT };

const handleSourceChange =
  (isHorizontalAxis: boolean) =>
  (value: AxisSourceValue | null): void => {
    if (isNull(value)) {
      dispatchAction(
        updateViewSettings({
          viewId: VIEW_ID,
          settings: isHorizontalAxis
            ? {
                selectedFieldNamesX: [],
                referenceTypeX: null,
                useParentAsX: false,
                sortOrderX: AxisSortOrder.DEFAULT,
                displayTextInViewX: null,
                additionalHeaderFieldNamesX: [],
              }
            : {
                selectedFieldNamesY: [],
                referenceTypeY: null,
                useParentAsY: false,
                sortOrderY: AxisSortOrder.DEFAULT,
                displayTextInViewY: null,
                additionalHeaderFieldNamesY: [],
              },
          persistent: true,
        })
      );
      return;
    }

    if (isParentSourceValue(value)) {
      dispatchAction(
        updateViewSettings({
          viewId: VIEW_ID,
          settings: isHorizontalAxis
            ? {
                selectedFieldNamesX: [],
                referenceTypeX: null,
                useParentAsX: true,
                sortOrderX: AxisSortOrder.DEFAULT,
                displayTextInViewX: null,
              }
            : {
                selectedFieldNamesY: [],
                referenceTypeY: null,
                useParentAsY: true,
                sortOrderY: AxisSortOrder.DEFAULT,
                displayTextInViewY: null,
              },
          persistent: true,
        })
      );
      return;
    }

    if (isRefSourceValue(value)) {
      dispatchAction(
        updateViewSettings({
          viewId: VIEW_ID,
          settings: isHorizontalAxis
            ? {
                selectedFieldNamesX: [],
                referenceTypeX: value.ref,
                useParentAsX: false,
                sortOrderX: AxisSortOrder.DEFAULT,
                displayTextInViewX: null,
              }
            : {
                selectedFieldNamesY: [],
                referenceTypeY: value.ref,
                useParentAsY: false,
                sortOrderY: AxisSortOrder.DEFAULT,
                displayTextInViewY: null,
              },
          persistent: true,
        })
      );
      return;
    }

    dispatchAction(
      updateViewSettings({
        viewId: VIEW_ID,
        settings: isHorizontalAxis
          ? {
              selectedFieldNamesX: [value.fieldName],
              referenceTypeX: null,
              useParentAsX: false,
              sortOrderX: AxisSortOrder.DEFAULT,
              displayTextInViewX: null,
              additionalHeaderFieldNamesX: [],
            }
          : {
              selectedFieldNamesY: [value.fieldName],
              referenceTypeY: null,
              useParentAsY: false,
              sortOrderY: AxisSortOrder.DEFAULT,
              displayTextInViewY: null,
              additionalHeaderFieldNamesY: [],
            },
        persistent: true,
      })
    );
  };

const getCurrentSourceValue = (
  viewSettings: ComponentMatrix2ViewSettings,
  isHorizontalAxis: boolean
): AxisSourceValue | null => {
  const useParent = isHorizontalAxis
    ? viewSettings.useParentAsX
    : viewSettings.useParentAsY;
  const fieldNames = isHorizontalAxis
    ? viewSettings.selectedFieldNamesX
    : viewSettings.selectedFieldNamesY;
  const referenceType = isHorizontalAxis
    ? viewSettings.referenceTypeX
    : viewSettings.referenceTypeY;

  if (useParent) return PARENT_SOURCE_VALUE;
  if (fieldNames.length > 0) return buildFieldSourceValue(fieldNames[0]);
  if (referenceType) return buildReferenceSourceValue(referenceType);
  return null;
};

const renderNavigatorOrderTooltip = (i18nLocale: I18nLocale) => (
  <Stack gap="xsmall">
    <Text variant="text2">
      {t(
        i18nLocale,
        'tabview:componentMatrix2.axis.sortDefault.tooltip',
        'Uses the sort order set in the navigator.'
      )}
    </Text>
    <Link
      href={KnowledgeBaseLink.SORTING_OF_COMPONENTS_IN_THE_VIEW}
      target="_blank"
      hideIcon
      typography="text2"
    >
      {t(
        i18nLocale,
        'tabview:componentMatrix2.axis.sortDefault.tooltipLink',
        'Learn how to change it.'
      )}
    </Link>
  </Stack>
);

const buildSortingOptions = (
  sourceValue: AxisSourceValue | null,
  i18nLocale: I18nLocale
): SelectOption<AxisSortOrder>[] => {
  const isFieldSelected = isFieldSourceValue(sourceValue);

  return [
    {
      value: AxisSortOrder.DEFAULT,
      label: isFieldSelected
        ? t(
            i18nLocale,
            'tabview:componentMatrix2.axis.sortDefault.field',
            'Default: field order'
          )
        : t(
            i18nLocale,
            'tabview:componentMatrix2.axis.sortDefault.reference',
            'Default: same as navigator'
          ),
      popoverContent: isFieldSelected
        ? undefined
        : () => renderNavigatorOrderTooltip(i18nLocale),
      popoverPlacement: isFieldSelected ? undefined : WithPopoverPlacement.TOP,
    },
    {
      value: AxisSortOrder.ALPHA_AZ,
      label: t(
        i18nLocale,
        'tabview:componentMatrix2.axis.sortAlphaAZ',
        'Alphabetical: 0-9, A-Z'
      ),
    },
    {
      value: AxisSortOrder.ALPHA_ZA,
      label: t(
        i18nLocale,
        'tabview:componentMatrix2.axis.sortAlphaZA',
        'Alphabetical: Z-A, 9-0'
      ),
    },
    {
      value: AxisSortOrder.COUNT_HIGH_LOW,
      label: t(
        i18nLocale,
        'tabview:componentMatrix2.axis.sortCountHighLow',
        'Total components in cell (high to low)'
      ),
    },
    {
      value: AxisSortOrder.COUNT_LOW_HIGH,
      label: t(
        i18nLocale,
        'tabview:componentMatrix2.axis.sortCountLowHigh',
        'Total components in cell (low to high)'
      ),
    },
  ];
};

const handleSortingChange =
  (isHorizontalAxis: boolean) =>
  (value: AxisSortOrder): void => {
    dispatchAction(
      updateViewSettings({
        viewId: VIEW_ID,
        settings: isHorizontalAxis
          ? { sortOrderX: value }
          : { sortOrderY: value },
        persistent: true,
      })
    );
  };

const handleDisplayTextChange =
  (isHorizontalAxis: boolean) =>
  (value: string | null): void => {
    dispatchAction(
      updateViewSettings({
        viewId: VIEW_ID,
        settings: isHorizontalAxis
          ? { displayTextInViewX: value }
          : { displayTextInViewY: value },
        persistent: true,
      })
    );
  };

const handleAdditionalHeaderFieldNamesChange =
  (isHorizontalAxis: boolean) =>
  (fieldNames: string[]): void => {
    dispatchAction(
      updateViewSettings({
        viewId: VIEW_ID,
        settings: isHorizontalAxis
          ? { additionalHeaderFieldNamesX: fieldNames }
          : { additionalHeaderFieldNamesY: fieldNames },
        persistent: true,
      })
    );
  };

/**
 * Sort the merged "Related Components" section by reference type name
 * first, then connected component type. Keeps the reference type the
 * primary axis the user scans for.
 */
const compareReferencedComponentOptions =
  (locale: Locale) =>
  (a: TraversedReferenceTypeInfo, b: TraversedReferenceTypeInfo) =>
    localeCompare(a.displayName, b.displayName, locale) ||
    localeCompare(
      a.linkedComponentTypeName ?? '',
      b.linkedComponentTypeName ?? '',
      locale
    );

/**
 * Inline chip rendering the connected component type as a colored badge
 * with the type's icon — matches the ARD-31884 design (image 1:
 * `Impacts ← [● Risk]`).
 *
 * Color recipe (per the spec):
 *   fill = getLightenedColor(stroke)          // canonical lightened fill
 *   text = ensureContrast(fill, stroke)       // stroke nudged for legibility
 *
 * Color and icon are pre-resolved at `reset()` time so the leftMenu
 * doesn't need access to `enhancedScopeData`.
 */
const ComponentTypeChip = ({
  typeName,
  style,
}: {
  typeName: string;
  style: ComponentTypeStyle | undefined;
}) => {
  if (!style) return <Text>{typeName}</Text>;
  const stroke = style.color;
  const fill = getLightenedColor(stroke);
  const text = ensureContrast(fill, stroke);
  return (
    <Tag style={{ backgroundColor: fill, color: text }}>
      <FlexBox gap="xsmall" align="center">
        <ComponentRepresentation
          {...style.representationData}
          style={{ color: text }}
        />
        <span style={{ color: text }}>{typeName}</span>
      </FlexBox>
    </Tag>
  );
};

/**
 * `[ReferenceType] [arrow] [ComponentTypeChip]` — the arrow direction
 * reflects the reference's direction relative to the focal component
 * (ARD-31884 image 1: `Impacts → Application`, `Impacts ← Risk`).
 */

const renderReferencedComponentLabel = (
  referenceType: TraversedReferenceTypeInfo,
  componentTypeStylesByName: Record<string, ComponentTypeStyle>
) => {
  const componentTypeName = referenceType.linkedComponentTypeName || '?';
  const arrow = referenceType.isOutgoing ? '→' : '←';
  return (
    <FlexBox gap="xsmall" align="center">
      <FlexBox flex={1} minWidth="0">
        <Text>{referenceType.displayName}</Text>
      </FlexBox>
      <Text>{arrow}</Text>
      <FlexBox flex={1} minWidth="0" justify="flex-end">
        <ComponentTypeChip
          typeName={componentTypeName}
          style={componentTypeStylesByName[componentTypeName]}
        />
      </FlexBox>
    </FlexBox>
  );
};

const buildReferenceSourceOption = (
  referenceType: TraversedReferenceTypeInfo,
  componentTypeStylesByName: Record<string, ComponentTypeStyle>
): AxisSourceOption => {
  const componentTypeName = referenceType.linkedComponentTypeName || '?';
  const arrow = referenceType.isOutgoing ? '→' : '←';

  return {
    value: buildReferenceSourceValue(referenceType),
    label: referenceType.displayName,
    description: `${arrow} ${componentTypeName}`,
    labelNode: renderReferencedComponentLabel(
      referenceType,
      componentTypeStylesByName
    ),
  };
};

const buildFieldSourceOption = (
  field: ComponentMatrix2ViewSettingsState['listFields'][number]
): AxisSourceOption => {
  const isConfidential = !!field.isConfidential;

  return {
    value: buildFieldSourceValue(field.name),
    label: field.label,
    isDisabled: isConfidential,
    rightContent: isConfidential ? getRedactedFieldDisplay() : undefined,
  };
};

const buildSourceOptions = (
  listFields: ComponentMatrix2ViewSettingsState['listFields'],
  referenceTypes: TraversedReferenceTypeInfo[],
  componentTypeStylesByName: Record<string, ComponentTypeStyle>,
  i18nLocale: I18nLocale
): AxisSourceGroup[] => {
  const groups: AxisSourceGroup[] = [];

  groups.push({
    groupKind: AxisSourceGroupKind.PARENT_RELATIONSHIP,
    label: '',
    options: [
      {
        value: PARENT_SOURCE_VALUE,
        label: t(
          i18nLocale,
          'tabview:componentMatrix.leftMenu.parentComponent',
          'Parent component'
        ),
      },
    ],
  });

  if (referenceTypes.length) {
    groups.push({
      groupKind: AxisSourceGroupKind.RELATED_COMPONENTS,
      label: '',
      options: referenceTypes.map(referenceType =>
        buildReferenceSourceOption(referenceType, componentTypeStylesByName)
      ),
    });
  }

  if (listFields.length) {
    groups.push({
      groupKind: AxisSourceGroupKind.FIELDS,
      label: '',
      options: listFields.map(buildFieldSourceOption),
    });
  }

  return groups;
};

type ComponentTypeSelectorConfigArgs = {
  componentTypes: string[];
  selectedComponentTypes: string[];
  i18nLocale: I18nLocale;
};

const buildComponentTypeSelectorConfig = ({
  componentTypes,
  selectedComponentTypes,
  i18nLocale,
}: ComponentTypeSelectorConfigArgs): SettingsConfig => {
  const isAllActive = selectedComponentTypes.length === 0;

  const setSelection = (next: string[]) =>
    dispatchAction(
      updateViewSettings({
        viewId: VIEW_ID,
        settings: { selectedComponentTypes: next },
        persistent: true,
      })
    );

  const allOption: DropdownItem = {
    name: 'cm2-component-type-all',
    label: t(i18nLocale, 'tabview:componentMatrix.leftMenu.allTypes', 'All'),
    type: DropdownOptionType.OPTION,
    isActive: isAllActive,
    onClick: () => setSelection([]),
  };

  const typeOptions: DropdownItem[] = componentTypes.map(typeName => ({
    name: `cm2-component-type-${typeName}`,
    label: typeName,
    type: DropdownOptionType.OPTION,
    isActive: !isAllActive && selectedComponentTypes.includes(typeName),
    onClick: () =>
      setSelection(selectedComponentTypes.includes(typeName) ? [] : [typeName]),
  }));

  return {
    id: 'componentTypeSelector',
    label: t(
      i18nLocale,
      'tabview:componentMatrix.leftMenu.componentType',
      'Components in cells'
    ),
    iconName: IconName.COMPONENT,
    options: [allOption, ...typeOptions],
    type: SettingsType.DROPDOWN,
    isDisabled: componentTypes.length === 0,
    maxDropdownSize: DropdownSize.M,
  };
};

type ComponentMatrix2LeftMenuArgs = {
  dependencies: ComponentMatrix2SettingsControlsDependencies;
  i18nLocale: I18nLocale;
};

const componentMatrix2LeftMenu = ({
  dependencies: {
    settingsBounds: { availableComponentTypes, canSetAxes },
    settableViewSettings,
    viewSettings,
    viewSettingsState: {
      listFields,
      additionalHeaderFieldsX,
      additionalHeaderFieldsY,
      traversedIncomingReferenceTypes,
      traversedOutgoingReferenceTypes,
      componentTypeStylesByName,
    },
    userLocale,
  },
  i18nLocale,
}: ComponentMatrix2LeftMenuArgs): SettingsConfig[] => {
  const sortedReferenceTypes = [
    ...traversedOutgoingReferenceTypes,
    ...traversedIncomingReferenceTypes,
  ].sort(compareReferencedComponentOptions(userLocale));

  const sourceOptions = buildSourceOptions(
    listFields,
    sortedReferenceTypes,
    componentTypeStylesByName,
    i18nLocale
  );

  const xSourceValue = getCurrentSourceValue(viewSettings, true);
  const ySourceValue = getCurrentSourceValue(viewSettings, false);

  return [
    buildComponentTypeSelectorConfig({
      componentTypes: availableComponentTypes,
      selectedComponentTypes: settableViewSettings.selectedComponentTypes,
      i18nLocale,
    }),
    {
      id: 'yFields',
      label: t(
        i18nLocale,
        'tabview:componentMatrix.leftMenu.row',
        'Group rows by'
      ),
      iconName: IconName.TABLE_ROWS,
      type: SettingsType.DROPDOWN,
      isKeepOpen: true,
      isDisabled: !canSetAxes,
      popoverText: canSetAxes
        ? undefined
        : t(
            i18nLocale,
            'tabview:componentMatrix.leftMenu.noOptionsPopover',
            'No list fields or references found in this context.'
          ),
      maxDropdownSize: DropdownSize.L,
      options: [
        buildAxisDropdownOption({
          axis: Axis.ROW,
          label: t(
            i18nLocale,
            'tabview:componentMatrix.leftMenu.row',
            'Group rows by'
          ),
          sourceValue: ySourceValue,
          sourceOptions,
          onSourceChange: handleSourceChange(false),
          sortingValue: viewSettings.sortOrderY ?? null,
          isSortingDisabled: ySourceValue === null,
          sortingOptions: buildSortingOptions(ySourceValue, i18nLocale),
          onSortingChange: handleSortingChange(false),
          i18nLocale,
          value: viewSettings.displayTextInViewY ?? null,
          onValueChange: handleDisplayTextChange(false),
          additionalHeaderFields: additionalHeaderFieldsY,
          additionalHeaderFieldNames: viewSettings.additionalHeaderFieldNamesY,
          onAdditionalHeaderFieldNamesChange:
            handleAdditionalHeaderFieldNamesChange(false),
        }),
      ],
    },
    {
      id: 'xFields',
      label: t(
        i18nLocale,
        'tabview:componentMatrix.leftMenu.column',
        'Group columns by'
      ),
      iconName: IconName.TABLE_COLUMNS,
      type: SettingsType.DROPDOWN,
      isKeepOpen: true,
      isDisabled: !canSetAxes,
      popoverText: canSetAxes
        ? undefined
        : t(
            i18nLocale,
            'tabview:componentMatrix.leftMenu.noOptionsPopover',
            'No list fields or references found in this context.'
          ),
      maxDropdownSize: DropdownSize.L,
      options: [
        buildAxisDropdownOption({
          axis: Axis.COLUMN,
          label: t(
            i18nLocale,
            'tabview:componentMatrix.leftMenu.column',
            'Group columns by'
          ),
          sourceValue: xSourceValue,
          sourceOptions,
          onSourceChange: handleSourceChange(true),
          sortingValue: viewSettings.sortOrderX ?? null,
          isSortingDisabled: xSourceValue === null,
          sortingOptions: buildSortingOptions(xSourceValue, i18nLocale),
          onSortingChange: handleSortingChange(true),
          i18nLocale,
          value: viewSettings.displayTextInViewX ?? null,
          onValueChange: handleDisplayTextChange(true),
          additionalHeaderFields: additionalHeaderFieldsX,
          additionalHeaderFieldNames: viewSettings.additionalHeaderFieldNamesX,
          onAdditionalHeaderFieldNamesChange:
            handleAdditionalHeaderFieldNamesChange(true),
        }),
      ],
    },
    {
      id: 'showEmptyValues',
      label: t(
        i18nLocale,
        'tabview:componentMatrix.leftMenu.showNoValue',
        'Show components with no value'
      ),
      iconName: IconName.NOT_INTERESTED,
      isActive: viewSettings.showEmptyValues,
      type: SettingsType.TOGGLE,
      popoverText: t(
        i18nLocale,
        'tabview:componentMatrix.leftMenu.showNoValuePopover',
        'Adds rows and columns for components with no value in the selected field, or no reference of the selected type.'
      ),
      onViewSettingsUpdate,
    },
    {
      id: 'colorRowColumnHeaderByComponent',
      label: t(
        i18nLocale,
        'tabview:componentMatrix.leftMenu.colorRowColumnHeader',
        'Color headers by component type'
      ),
      iconName: IconName.PALETTE,
      isActive: viewSettings.colorRowColumnHeaderByComponent,
      type: SettingsType.TOGGLE,
      popoverText: t(
        i18nLocale,
        'tabview:componentMatrix.leftMenu.colorRowColumnHeaderPopover',
        'Color row and column headers using the component type color. When off, all headers are displayed in gray.'
      ),
      onViewSettingsUpdate,
    },
  ];
};

export { componentMatrix2LeftMenu };
